import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Tag, Space, Input, Select, message, Modal, Drawer,
  Row, Col, Statistic, Form, Tooltip, Popconfirm, Descriptions, Timeline
} from 'antd';
import {
  ReloadOutlined, FilePdfOutlined, MailOutlined, StopOutlined,
  EyeOutlined, RollbackOutlined, FileSearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const STATUS_CONFIG = {
  issued:       { color: 'blue',    text: '待使用' },
  pending_use:  { color: 'orange',  text: '锁定中' },
  used:         { color: 'green',   text: '已使用' },
  expired:      { color: 'default', text: '已过期' },
  cancelled:    { color: 'red',     text: '已作废' },
};

const formatNZD = (n) => `NZD $${Number(n || 0).toFixed(2)}`;
const formatDate = (d) => d ? dayjs(d).format('YYYY-MM-DD') : '-';
const formatDateTime = (d) => d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '-';

export default function VoucherManagement() {
  const userInfo = authService.getUserInfo();
  const role = userInfo?.role;
  const isFinance = role === 'admin' || role === 'finance';
  const showAmount = isFinance;
  // 作废：admin/finance/operations 都可；撤销自愿退团：仅 admin/finance（动客人状态需谨慎）
  const canCancel = ['admin', 'finance', 'operations'].includes(role);
  const canRestore = role === 'admin' || role === 'finance';

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({});
  const [detail, setDetail] = useState(null);

  // 作废/撤销 弹窗（受控）
  const [cancelOpen, setCancelOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [actionRecord, setActionRecord] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, pageSize };
      const res = await axios.get(`${API_BASE}/vouchers`, {
        params, headers: authService.getAuthHeaders()
      });
      setList(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      message.error('获取列表失败：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/vouchers/stats`, {
        headers: authService.getAuthHeaders()
      });
      setStats(res.data || {});
    } catch (err) {
      console.error('统计失败', err);
    }
  }, []);

  useEffect(() => {
    fetchList();
    fetchStats();
  }, [fetchList, fetchStats]);

  // 行操作
  const handleViewPdf = async (record) => {
    try {
      const res = await axios.get(`${API_BASE}/vouchers/${record.id}/pdf`, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      message.error('打开 PDF 失败');
    }
  };

  const handleResend = (record) => {
    let toEmail = record.sentToEmail || record.tourist?.salesperson?.email || '';
    Modal.confirm({
      title: `重发 Voucher ${record.code}`,
      content: (
        <div style={{ paddingTop: 8 }}>
          <div style={{ marginBottom: 8 }}>接收邮箱：</div>
          <Input
            defaultValue={toEmail}
            onChange={(e) => { toEmail = e.target.value; }}
            placeholder="输入接收邮箱"
          />
        </div>
      ),
      okText: '发送',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await axios.post(
            `${API_BASE}/vouchers/${record.id}/resend`,
            { sentToEmail: toEmail },
            { headers: authService.getAuthHeaders() }
          );
          if (res.data.success) {
            message.success('邮件已重发');
            fetchList();
          } else {
            message.error('发送失败：' + (res.data.email?.error || '未知错误'));
          }
        } catch (err) {
          message.error('重发失败：' + (err.response?.data?.error || err.message));
        }
      }
    });
  };

  const handleCancel = (record) => {
    setActionRecord(record);
    setActionReason('');
    setCancelOpen(true);
  };

  const handleRestore = (record) => {
    setActionRecord(record);
    setActionReason('');
    setRestoreOpen(true);
  };

  const submitCancel = async () => {
    if (!actionReason.trim()) {
      message.warning('请填写作废原因');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API_BASE}/vouchers/${actionRecord.id}/cancel`,
        { reason: actionReason },
        { headers: authService.getAuthHeaders() }
      );
      message.success('已作废');
      setCancelOpen(false);
      fetchList();
      fetchStats();
    } catch (err) {
      message.error('作废失败：' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const submitRestore = async () => {
    if (!actionReason.trim()) {
      message.warning('请填写撤销原因');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API_BASE}/vouchers/${actionRecord.id}/restore`,
        { reason: actionReason },
        { headers: authService.getAuthHeaders() }
      );
      message.success('已撤销');
      setRestoreOpen(false);
      fetchList();
      fetchStats();
    } catch (err) {
      message.error('撤销失败：' + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: 'Voucher 编号',
      dataIndex: 'code',
      width: 170,
      fixed: 'left',
      render: (v, r) => (
        <a onClick={() => setDetail(r)} style={{ fontFamily: 'Courier New, monospace', fontWeight: 500 }}>
          {v}
        </a>
      )
    },
    {
      title: '持有人',
      key: 'holder',
      width: 180,
      render: (_, r) => (
        <div>
          <div>{r.holderName}</div>
          <div style={{ color: '#999', fontSize: 12 }}>
            {r.holderIdNumber ? r.holderIdNumber.slice(0, 1) + '****' + r.holderIdNumber.slice(-4) : '-'}
          </div>
        </div>
      )
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 120,
      render: (v, r) => formatNZD(v)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v) => {
        const c = STATUS_CONFIG[v] || { color: 'default', text: v };
        return <Tag color={c.color}>{c.text}</Tag>;
      }
    },
    {
      title: '来源团',
      key: 'sourceTour',
      width: 220,
      render: (_, r) => {
        const t = r.sourceTour;
        if (!t) return '-';
        const name = t.destination?.name || t.productName || '-';
        return (
          <div>
            <div>{name}</div>
            <div style={{ color: '#999', fontSize: 12 }}>
              出发：{formatDate(t.departureDate)}
            </div>
          </div>
        );
      }
    },
    {
      title: '签发人 / 时间',
      key: 'issued',
      width: 160,
      render: (_, r) => (
        <div>
          <div>{r.issuer?.name || '-'}</div>
          <div style={{ color: '#999', fontSize: 12 }}>{formatDateTime(r.issuedAt)}</div>
        </div>
      )
    },
    {
      title: '销售（邮件）',
      key: 'salesperson',
      width: 180,
      render: (_, r) => {
        const s = r.tourist?.salesperson;
        const sentColor = r.emailStatus === 'sent' ? '#52c41a' : r.emailStatus === 'failed' ? '#ff4d4f' : '#999';
        const sentText = r.emailStatus === 'sent' ? '✓ 已发' : r.emailStatus === 'failed' ? '✗ 失败' : '未发';
        return (
          <div>
            <div>{s?.name || '-'}</div>
            <div style={{ color: sentColor, fontSize: 12 }}>{sentText}{r.sentCount > 1 ? ` (${r.sentCount}次)` : ''}</div>
          </div>
        );
      }
    },
    {
      title: '有效期至',
      dataIndex: 'expiresAt',
      width: 130,
      render: (v, r) => {
        if (!v) return '-';
        const date = dayjs(v);
        const days = date.diff(dayjs(), 'day');
        let color = '#333';
        if (r.status === 'expired') color = '#999';
        else if (days < 0) color = '#999';
        else if (days <= 30) color = '#fa8c16';
        return <span style={{ color }}>{date.format('YYYY-MM-DD')}</span>;
      }
    },
    {
      title: '已用于',
      key: 'usedIn',
      width: 220,
      render: (_, r) => {
        if (r.status !== 'used' || !r.usedInTour) return '-';
        const tourName = r.usedInTour.destination?.name || r.usedInTour.productName || '-';
        return (
          <div>
            <div>{tourName}</div>
            <div style={{ color: '#999', fontSize: 12 }}>
              出发：{formatDate(r.usedInTour.departureDate)}
            </div>
          </div>
        );
      }
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 240,
      render: (_, r) => (
        <Space size={4} wrap>
          <Tooltip title="查看 PDF">
            <Button size="small" icon={<FilePdfOutlined />} onClick={() => handleViewPdf(r)} />
          </Tooltip>
          <Tooltip title="重发邮件">
            <Button
              size="small"
              icon={<MailOutlined />}
              disabled={r.status === 'cancelled'}
              onClick={() => handleResend(r)}
            />
          </Tooltip>
          {canCancel && r.status !== 'cancelled' && r.status !== 'used' && (
            <Tooltip title="作废 Voucher">
              <Button size="small" danger icon={<StopOutlined />} onClick={() => handleCancel(r)} />
            </Tooltip>
          )}
          {canRestore && r.status !== 'cancelled' && r.status !== 'used' && (
            <Tooltip title="撤销自愿退团">
              <Button size="small" icon={<RollbackOutlined />} onClick={() => handleRestore(r)} />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 16 }}>
      {/* 顶部统计卡 — 高度统一 */}
      <Row gutter={16} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} bodyStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
            <Statistic title="累计签发" value={stats.issued?.count || 0} suffix="张" />
            <div style={{ marginTop: 8, color: '#999', fontSize: 12, minHeight: 18 }}>
              {showAmount ? `总额：${formatNZD(stats.issued?.sum)}` : ' '}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} bodyStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
            <Statistic
              title={showAmount ? '资金敞口（待兑付）' : '待使用'}
              value={showAmount ? Number(stats.outstanding?.sum || stats.issued?.sum || 0).toFixed(2) : (stats.issued?.count || 0)}
              prefix={showAmount ? 'NZD $' : ''}
              valueStyle={{ color: '#fa8c16' }}
            />
            <div style={{ marginTop: 8, color: '#999', fontSize: 12, minHeight: 18 }}>
              {showAmount
                ? `${stats.issued?.count || 0} 张待使用`
                : `30 天内过期：${stats.expiringSoon || 0} 张`}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} bodyStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
            <Statistic title="已使用" value={stats.used?.count || 0} suffix="张" valueStyle={{ color: '#52c41a' }} />
            <div style={{ marginTop: 8, color: '#999', fontSize: 12, minHeight: 18 }}>
              {showAmount ? `核销：${formatNZD(stats.used?.sum)}` : ' '}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} bodyStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
            <Statistic title="已过期 / 作废" value={stats.expired?.count || 0} suffix="张" valueStyle={{ color: '#999' }} />
            <div style={{ marginTop: 8, color: '#999', fontSize: 12, minHeight: 18 }}>
              {showAmount ? `金额：${formatNZD(stats.expired?.sum)}` : ' '}
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        title={<Space><FileSearchOutlined />Voucher 列表</Space>}
        extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchList(); fetchStats(); }}>刷新</Button>}
      >
        <Form layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item label="状态">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 140 }}
              onChange={(v) => { setFilters((f) => ({ ...f, status: v })); setPage(1); }}
              options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.text }))}
            />
          </Form.Item>
          <Form.Item label="编号">
            <Input.Search
              allowClear
              placeholder="WG-V-..."
              style={{ width: 200 }}
              onSearch={(v) => { setFilters((f) => ({ ...f, code: v || undefined })); setPage(1); }}
            />
          </Form.Item>
          <Form.Item label="持有人">
            <Input.Search
              allowClear
              placeholder="姓名 / 证件号"
              style={{ width: 200 }}
              onSearch={(v) => { setFilters((f) => ({ ...f, holder: v || undefined })); setPage(1); }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              onClick={() => { setFilters((f) => ({ ...f, expiringSoon: f.expiringSoon === '1' ? undefined : '1' })); setPage(1); }}
              type={filters.expiringSoon === '1' ? 'primary' : 'default'}
            >
              30 天内过期
            </Button>
          </Form.Item>
        </Form>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={list}
          size="small"
          scroll={{ x: 1500 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); }
          }}
        />
      </Card>

      {/* 详情抽屉 */}
      <Drawer
        title={detail ? `Voucher 详情 · ${detail.code}` : 'Voucher 详情'}
        open={!!detail}
        onClose={() => setDetail(null)}
        width={640}
      >
        {detail && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Voucher 编号">{detail.code}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_CONFIG[detail.status]?.color}>{STATUS_CONFIG[detail.status]?.text}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="持有人">{detail.holderName}</Descriptions.Item>
              <Descriptions.Item label="证件号（脱敏）">
                {detail.holderIdNumber ? detail.holderIdNumber.slice(0, 1) + '****' + detail.holderIdNumber.slice(-4) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="面值">{formatNZD(detail.amount)}</Descriptions.Item>
              <Descriptions.Item label="来源团">
                {detail.sourceTour?.destination?.name || detail.sourceTour?.productName || '-'}
                <span style={{ color: '#999' }}> · 团 #{detail.sourceTour?.id} · {formatDate(detail.sourceTour?.departureDate)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="签发原因">
                <div style={{ whiteSpace: 'pre-wrap' }}>{detail.withdrawalReason || '-'}</div>
              </Descriptions.Item>
              <Descriptions.Item label="签发人">{detail.issuer?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="销售">
                {detail.tourist?.salesperson?.name || '-'}
                {detail.tourist?.salesperson?.email && (
                  <span style={{ color: '#999' }}> · {detail.tourist.salesperson.email}</span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="发送邮箱">{detail.sentToEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label="有效期">
                {formatDate(detail.issuedAt)} ~ {formatDate(detail.expiresAt)}
              </Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginTop: 24 }}>生命周期</h4>
            <Timeline
              items={[
                {
                  color: 'blue',
                  children: (
                    <>
                      <div><strong>签发</strong> · {formatDateTime(detail.issuedAt)}</div>
                      <div style={{ color: '#666', fontSize: 12 }}>
                        签发人：{detail.issuer?.name || '-'}
                      </div>
                    </>
                  )
                },
                detail.sentAt && {
                  color: 'green',
                  children: (
                    <>
                      <div><strong>邮件已发送</strong> · {formatDateTime(detail.sentAt)}</div>
                      <div style={{ color: '#666', fontSize: 12 }}>
                        到 {detail.sentToEmail}（共 {detail.sentCount} 次）
                      </div>
                    </>
                  )
                },
                detail.usedAt && {
                  color: 'green',
                  children: (
                    <>
                      <div><strong>核销</strong> · {formatDateTime(detail.usedAt)}</div>
                      <div style={{ color: '#666', fontSize: 12 }}>
                        用于团 #{detail.usedInTour?.id}
                      </div>
                    </>
                  )
                },
                detail.cancelledAt && {
                  color: 'red',
                  children: (
                    <>
                      <div><strong>作废</strong> · {formatDateTime(detail.cancelledAt)}</div>
                      <div style={{ color: '#666', fontSize: 12 }}>
                        操作人：{detail.canceller?.name || '-'} · {detail.cancelReason}
                      </div>
                    </>
                  )
                }
              ].filter(Boolean)}
            />

            <Space style={{ marginTop: 16 }}>
              <Button icon={<FilePdfOutlined />} onClick={() => handleViewPdf(detail)}>查看 PDF</Button>
              {detail.status !== 'cancelled' && (
                <Button icon={<MailOutlined />} onClick={() => handleResend(detail)}>重发邮件</Button>
              )}
              {canCancel && detail.status !== 'cancelled' && detail.status !== 'used' && (
                <Button danger icon={<StopOutlined />} onClick={() => handleCancel(detail)}>作废</Button>
              )}
              {canRestore && detail.status !== 'cancelled' && detail.status !== 'used' && (
                <Button icon={<RollbackOutlined />} onClick={() => handleRestore(detail)}>撤销自愿退团</Button>
              )}
            </Space>
          </>
        )}
      </Drawer>

      {/* 作废 Voucher Modal */}
      <Modal
        title={actionRecord ? `作废 Voucher ${actionRecord.code}？` : '作废 Voucher'}
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        onOk={submitCancel}
        confirmLoading={actionLoading}
        okType="danger"
        okText="确认作废"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: '#ff4d4f' }}>
          作废后该券将无法核销，请填写原因：
        </div>
        <Input.TextArea
          rows={3}
          value={actionReason}
          onChange={(e) => setActionReason(e.target.value)}
          placeholder="作废原因（必填）"
          autoFocus
        />
      </Modal>

      {/* 撤销自愿退团 Modal */}
      <Modal
        title={actionRecord ? `撤销自愿退团 ${actionRecord.code}？` : '撤销自愿退团'}
        open={restoreOpen}
        onCancel={() => setRestoreOpen(false)}
        onOk={submitRestore}
        confirmLoading={actionLoading}
        okText="确认撤销"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: '#fa8c16' }}>
          撤销后客人状态将恢复为「在团」，关联 Voucher 同步作废。
        </div>
        <Input.TextArea
          rows={3}
          value={actionReason}
          onChange={(e) => setActionReason(e.target.value)}
          placeholder="撤销原因（必填）"
          autoFocus
        />
      </Modal>
    </div>
  );
}
